// src/components/UploadChallengeModal.js
import React, { useEffect, useRef, useState } from "react";
import pb from "../lib/pb";
import { useAuth } from "../contexts/AuthContext";

// Helper: simple key + AES encrypt (copied from your pattern)
function genKey() {
  const arr = window.crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...arr));
}
async function encryptText(base64Key, text) {
  const keyBytes = Uint8Array.from(atob(base64Key), (c) => c.charCodeAt(0));
  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyBytes,
    "AES-GCM",
    true,
    ["encrypt"]
  );
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder().encode(text);
  const ct = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, cryptoKey, enc);
  const combined = new Uint8Array(iv.byteLength + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), iv.byteLength);
  return btoa(String.fromCharCode(...combined));
}

/*
Props:
 - open (bool)
 - onClose()
 - onPosted(challenge) optional callback
 - initialMode: 'image'|'video'|'audio'|'text' (optional)
*/
export default function UploadChallengeModal({ open, onClose, onPosted, initialMode = "image" }) {
  const { user } = useAuth();

  const [mode, setMode] = useState(initialMode); // image | video | audio | text
  const [fileList, setFileList] = useState([]); // for multiple images or single media
  const [mediaBlob, setMediaBlob] = useState(null); // last recorded/selected blob
  const [textContent, setTextContent] = useState("");
  const [title, setTitle] = useState("");
  const [license, setLicense] = useState("Public");
  const [recording, setRecording] = useState(false);
  const [timer, setTimer] = useState(0);

  // camera/audio refs
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerRef = useRef(null);

  // preview URL
  const [previewURL, setPreviewURL] = useState(null);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      resetAll();
    } else {
      cleanupStream();
    }
    
  }, [open]);

  const resetAll = () => {
    setFileList([]);
    setMediaBlob(null);
    setTextContent("");
    setTitle("");
    setLicense("Public");
    setRecording(false);
    setTimer(0);
    if (previewURL) { URL.revokeObjectURL(previewURL); setPreviewURL(null); }
  };

  const cleanupStream = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
    } catch {}
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    clearInterval(timerRef.current);
  };

  // Select files from device
  const onSelectFiles = (ev) => {
    const files = Array.from(ev.target.files || []);
    // if images mode, allow multiple; otherwise take the first
    if (mode === "image") {
      setFileList(prev => [...prev, ...files]);
      // create preview for first image
      if (files[0]) {
        const url = URL.createObjectURL(files[0]);
        setPreviewURL(url);
      }
    } else {
      const f = files[0];
      setMediaBlob(f);
      setPreviewURL(URL.createObjectURL(f));
    }
  };

  // Start camera (image or video)
  const startCamera = async () => {
    try {
      cleanupStream();
      const constraints = { video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 1280 } }, audio: (mode === "video") };
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.muted = true;
        videoRef.current.play().catch(()=>{});
      }
    } catch (err) {
      console.error("camera error", err);
      alert("Cannot access camera/mic. Check permissions.");
    }
  };

  // Capture photo (single frame) � for multiple images you can call this repeatedly
  const capturePhoto = async () => {
    if (!streamRef.current || !videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 720;
    canvas.height = v.videoHeight || 1280;
    const ctx = canvas.getContext("2d");
    // mirror for selfie look
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.92));
    setFileList(prev => [...prev, blob]);
    if (previewURL) { URL.revokeObjectURL(previewURL); }
    setPreviewURL(URL.createObjectURL(blob));
    // stop camera optionally
    //cleanupStream();
  };

  // Start recording (video limited to 60 seconds vertical)
  const startRecording = async () => {
    if (recording) return;
    try {
      if (!streamRef.current) await startCamera();
      const s = streamRef.current;
      recordedChunksRef.current = [];
      const options = {};
      if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) options.mimeType = "video/webm;codecs=vp9";
      else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) options.mimeType = "video/webm;codecs=vp8";
      else if (MediaRecorder.isTypeSupported("video/mp4")) options.mimeType = "video/mp4";

      const mr = new MediaRecorder(s, options);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size) recordedChunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: recordedChunksRef.current[0]?.type || "video/webm" });
        setMediaBlob(blob);
        if (previewURL) URL.revokeObjectURL(previewURL);
        setPreviewURL(URL.createObjectURL(blob));
        recordedChunksRef.current = [];
        cleanupStream();
      };

      mr.start(500);
      setRecording(true);
      setTimer(0);
      timerRef.current = setInterval(() => {
        setTimer(t => {
          if (mode === "video" && t + 1 >= 60) { // 60 seconds limit
            try { if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop(); } catch {}
            clearInterval(timerRef.current);
            return 60;
          }
          return t + 1;
        });
      }, 1000);

    } catch (err) {
      console.error("record start error", err);
      alert("Recording failed to start.");
      setRecording(false);
    }
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
    } catch (err) { console.error(err); }
    clearInterval(timerRef.current);
    setRecording(false);
  };

  // Start audio-only recording
  const startAudioRecording = async () => {
    if (recording) return;
    try {
      cleanupStream();
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = s;
      recordedChunksRef.current = [];
      const options = { mimeType: "audio/webm" };
      const mr = new MediaRecorder(s, options);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data && e.data.size) recordedChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: recordedChunksRef.current[0]?.type || "audio/webm" });
        setMediaBlob(blob);
        if (previewURL) URL.revokeObjectURL(previewURL);
        setPreviewURL(URL.createObjectURL(blob));
        recordedChunksRef.current = [];
        cleanupStream();
      };
      mr.start(500);
      setRecording(true);
      setTimer(0);
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } catch (err) {
      console.error(err);
      alert("Audio recording failed.");
    }
  };

  // Post captured/uploaded challenge to PocketBase (buzzing_challenges)
  const postChallenge = async () => {
    if (!user) return alert("Login required");
    try {
      const publicKey = genKey();
      const privateKey = genKey();
      const form = new FormData();
      form.append("title", title || "");
      form.append("description", textContent || "");
      form.append("licenseType", license);
      form.append("publicKey", publicKey);
      form.append("privateKey", privateKey);
      form.append("author", user.id);

      // set content/encryption like your CreatePost
      if (license === "Private") {
        const enc = await encryptText(privateKey, textContent || title || "");
        form.append("encryptedContent", enc);
        form.append("content", "[private]");
      } else {
        form.append("content", textContent || title || "");
      }

      // media handling per mode
      if (mode === "image") {
        // support multiple images in fileList
        for (let i = 0; i < fileList.length; i++) {
          const f = fileList[i];
          // ensure it's a File/Blob
          form.append("media", f, `img_${Date.now()}_${i}.jpg`);
        }
        form.append("mediaType", "Image");
      } else if (mode === "video") {
        // recorded blob or uploaded
        const blob = mediaBlob;
        if (!blob) return alert("No video recorded/selected.");
        form.append("media", blob, `vid_${Date.now()}.webm`);
        form.append("mediaType", "Video");
      } else if (mode === "audio") {
        const blob = mediaBlob;
        if (!blob) return alert("No audio recorded/selected.");
        form.append("media", blob, `aud_${Date.now()}.webm`);
        form.append("mediaType", "Music");
      } else if (mode === "text") {
        form.append("mediaType", "Text");
      }

      // metadata for challenge type
      form.append("challengeType", mode); // image|video|audio|text

      // create record
      const created = await pb.collection("buzzing_challenges").create(form);
      // increment analytics: create view/accept/vote counters if needed
      // You can create docs in separate collections to track interactions

      alert("Challenge posted!");
      if (onPosted) onPosted(created);
      // reset and close
      resetAll();
      onClose && onClose();

    } catch (err) {
      console.error("postChallenge error", err);
      alert("Failed to post challenge: " + (err?.message || err));
    }
  };

  // UI
  if (!open) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999,
      background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <div style={{ width: "94%", maxWidth: 920, background: "#0b0b0d", borderRadius: 12, padding: 16, color: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>Create Challenge</h3>
          <div>
            <button onClick={() => { cleanupStream(); onClose && onClose(); }} style={{ background: "transparent", border: "1px solid #333", color: "#fff", padding: "8px 10px", borderRadius: 8 }}>Close</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 160 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button className={`mode-btn ${mode==="image"?"active":""}`} onClick={() => { setMode("image"); cleanupStream(); }}>?? Image</button>
              <button className={`mode-btn ${mode==="video"?"active":""}`} onClick={() => { setMode("video"); cleanupStream(); }}>?? Video (60s)</button>
              <button className={`mode-btn ${mode==="audio"?"active":""}`} onClick={() => { setMode("audio"); cleanupStream(); }}>?? Audio</button>
              <button className={`mode-btn ${mode==="text"?"active":""}`} onClick={() => { setMode("text"); cleanupStream(); }}>?? Text</button>
            </div>
            <hr style={{ marginTop: 12, borderColor: "#222" }} />
            <div style={{ marginTop: 8 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Challenge title" style={{ width: "100%", padding: 8, borderRadius: 8, background: "#0f0f12", color: "#fff", border: "1px solid #333" }} />
            </div>
          </div>

          <div style={{ flex: 1 }}>
            {/* PREVIEW AREA */}
            <div style={{ height: 360, background: "#050507", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
              {/* live camera preview */}
              {(mode === "image" || mode === "video") && !previewURL && (
                <video ref={videoRef} style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} autoPlay muted playsInline />
              )}

              {mode === "audio" && !previewURL && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <audio ref={audioRef} controls style={{ width: 300 }} />
                  <div style={{ marginTop: 10, color: "#bbb" }}>Microphone ready</div>
                </div>
              )}

              {previewURL && (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {(mode === "image") && <img src={previewURL} alt="preview" style={{ maxWidth: "100%", maxHeight: "100%" }} />}
                  {(mode === "video") && <video src={previewURL} controls style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                  {(mode === "audio") && <audio src={previewURL} controls style={{ width: "100%" }} />}
                </div>
              )}

              {/* helper overlay */}
              {!previewURL && (
                <div style={{ position: "absolute", bottom: 12, left: 12, display: "flex", gap: 8, alignItems: "center" }}>
                  {(mode === "image") && (
                    <>
                      <input id="file-img" type="file" accept="image/*" multiple onChange={onSelectFiles} style={{ display: "none" }} />
                      <button onClick={() => document.getElementById("file-img").click()} style={{ padding: "8px 12px" }}>Upload images</button>
                      <button onClick={startCamera} style={{ padding: "8px 12px" }}>Open Camera</button>
                      <button onClick={capturePhoto} style={{ padding: "8px 12px" }}>Capture Photo</button>
                    </>
                  )}

                  {(mode === "video") && (
                    <>
                      <input id="file-vid" type="file" accept="video/*" onChange={onSelectFiles} style={{ display: "none" }} />
                      <button onClick={() => document.getElementById("file-vid").click()}>Upload video</button>
                      {!recording ? <button onClick={startRecording}>Start Rec</button> : <button onClick={stopRecording}>Stop</button>}
                      <div style={{ color: "#bbb", marginLeft: 8 }}>{recording ? `Rec ${timer}s` : "Idle"}</div>
                    </>
                  )}

                  {(mode === "audio") && (
                    <>
                      <input id="file-aud" type="file" accept="audio/*" onChange={onSelectFiles} style={{ display: "none" }} />
                      <button onClick={() => document.getElementById("file-aud").click()}>Upload audio</button>
                      {!recording ? <button onClick={startAudioRecording}>Record audio</button> : <button onClick={stopRecording}>Stop</button>}
                      <div style={{ color: "#bbb", marginLeft: 8 }}>{recording ? `Rec ${timer}s` : "Idle"}</div>
                    </>
                  )}

                  {(mode === "text") && (
                    <div style={{ color: "#bbb" }}>Text-only challenge</div>
                  )}
                </div>
              )}
            </div>

            {/* editor panel */}
            <div style={{ marginTop: 12, display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <textarea value={textContent} onChange={e => setTextContent(e.target.value)} placeholder="Write a description (optional)..." style={{ width: "100%", minHeight: 120, padding: 10, borderRadius: 8, background: "#0f0f12", color: "#fff", border: "1px solid #222" }} />
                <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
                  <label style={{ color: "#ccc" }}>
                    <input type="radio" checked={license === "Public"} onChange={() => setLicense("Public")} /> Public
                  </label>
                  <label style={{ color: "#ccc" }}>
                    <input type="radio" checked={license === "Private"} onChange={() => setLicense("Private")} /> Private
                  </label>
                </div>
              </div>

              <div style={{ width: 220, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: 12, color: "#bbb" }}>Preview files</div>
                <div style={{ background: "#07070a", padding: 8, borderRadius: 8, minHeight: 120 }}>
                  {fileList.length > 0 ? (
                    <div style={{ display: "flex", gap: 8, overflowX: "auto" }}>
                      {fileList.map((f, i) => {
                        const url = (f instanceof Blob || f instanceof File) ? URL.createObjectURL(f) : null;
                        return (
                          <div key={i} style={{ width: 80 }}>
                            <img src={url} alt="" style={{ width: "100%", height: 60, objectFit: "cover", borderRadius: 6 }} />
                            <div style={{ fontSize: 11, marginTop: 6, color: "#ddd" }}>#{i + 1}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : mediaBlob ? (
                    <div>
                      <div style={{ fontSize: 13 }}>{mediaBlob.type}</div>
                      <div style={{ marginTop: 8 }}><small style={{ color: "#9aa" }}>{(mediaBlob.size / 1024 / 1024).toFixed(2)} MB</small></div>
                    </div>
                  ) : (
                    <div style={{ color: "#777" }}>No files</div>
                  )}
                </div>

                <button onClick={postChallenge} style={{ background: "#00c48c", padding: "10px 12px", borderRadius: 8, border: "none", color: "#071012", cursor: "pointer" }}>Post Challenge</button>
                <button onClick={() => { resetAll(); }} style={{ background: "transparent", padding: "8px 10px", borderRadius: 8, border: "1px solid #333", color: "#fff" }}>Reset</button>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}